import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { initPerformanceMonitoring } from './utils/performance';

// Theme bootstrap (applies before first render)
(() => {
  try {
    const stored = localStorage.getItem('pww_theme');
    const theme = stored === 'dark' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.style.colorScheme = theme;
  } catch {
    document.documentElement.setAttribute('data-theme', 'light');
    document.documentElement.style.colorScheme = 'light';
  }
})();

if (!window.__PWW_RESIZE_OBSERVER_LOOP_FILTER__) {
// Chrome can emit a ResizeObserver loop error which React's dev overlay may surface
// as a runtime exception. This prevents the overlay from blocking the UI.
const resizeObserverLoopError = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i;
const shouldIgnoreResizeObserverLoopError = (eventOrMessage) => {
  if (!eventOrMessage) return false;
  if (typeof eventOrMessage === 'string') return resizeObserverLoopError.test(eventOrMessage);

  const message =
    eventOrMessage?.message ||
    eventOrMessage?.reason?.message ||
    eventOrMessage?.error?.message ||
    '';

  return resizeObserverLoopError.test(message);
};

const swallowIfResizeObserverLoop = (event) => {
  if (!shouldIgnoreResizeObserverLoopError(event)) return;

  // Prevent CRA/React overlay from treating it as a fatal runtime error.
  if (typeof event?.preventDefault === 'function') event.preventDefault();
  if (typeof event?.stopPropagation === 'function') event.stopPropagation();
  if (typeof event?.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
};

// Use capture phase so we run before the dev overlay listeners.
window.addEventListener('error', swallowIfResizeObserverLoop, true);
window.addEventListener('unhandledrejection', swallowIfResizeObserverLoop, true);

// CRA's overlay often hooks into console.error instead of relying solely on window.onerror.
// Filter the known benign ResizeObserver loop message so it doesn't break the dev experience.
const originalConsoleError = console.error;
console.error = (...args) => {
  const hasResizeObserverLoopMsg = (args || []).some((arg) => {
    if (!arg) return false;
    if (typeof arg === 'string') return resizeObserverLoopError.test(arg);
    if (typeof arg?.message === 'string') return resizeObserverLoopError.test(arg.message);
    return false;
  });

  if (hasResizeObserverLoopMsg) return;

  originalConsoleError(...args);
};
}

// Section 31 — Monitoring: collect Web Vitals and API latency in production
initPerformanceMonitoring();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <App />
);

// Register custom service worker that doesn't cache API endpoints
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Unregister all existing service workers first (CRA's defaults)
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        registration.unregister();
      }
    });
    
    // Register our custom service worker
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        console.log('[App] Custom service worker registered:', registration);
      })
      .catch((error) => {
        console.error('[App] Service worker registration failed:', error);
      });
  });
}
